import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { RevChip, RevModal, RevPagination, RevProgress, SHARE_STATUS_TONE } from '../ds';
import { matchesEventsSearch } from '../../helpers/eventsSearch';

const ITEMS_PER_PAGE = 5;

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

// La gráfica no depende de la tabla: todos los colaboradores, de más a menos fotos.
const PhotosByUserChart = ({ users }) => {
    const { t } = useTranslation();
    // Chart.js pinta en canvas y no resuelve var(): se lee --ink-900 del DOM al
    // montar; el hex es el MISMO valor de tokens.css, solo como respaldo.
    const [barColor, setBarColor] = useState('#1D1D1B'); // ds-lint: hex-ok (= --ink-900)

    useEffect(() => {
        const resolved = getComputedStyle(document.documentElement).getPropertyValue('--ink-900').trim();
        if (resolved) setBarColor(resolved);
    }, []);

    const byPhotos = [...users].sort((a, b) => (b.photos || 0) - (a.photos || 0));
    const labels = byPhotos.map(user => user.email);
    const quantities = byPhotos.map(user => user.photos);

    const options = {
        title: {
            display: false,
        },
        legend: {
            display: false,
        },
        plugins: {
            legend: {
                display: false,
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                }
            },
            y: {
                grid: {
                    display: false,
                },
                beginAtZero: true,
                // Solo enteros y pocas marcas, haya 3 fotos o 3.000.
                ticks: {
                    precision: 0,
                    maxTicksLimit: 5
                }
            }
        },
        maintainAspectRatio: false
    };

    const data = {
        labels: labels,
        datasets: [
            {
                label: t('eventStats.modal.noPhotos'),
                data: quantities,
                backgroundColor: barColor,
                borderRadius: 20
            }
        ],
    };

    return <div className="rev-chart-box"><Bar options={options} data={data} /></div>;
}

const PhotosByUserTable = ({ pagedUsers, handleSort, sortColumn, sortDirection }) => {
    const { t } = useTranslation();

    const sortIcon = (column) => {
        if (sortColumn !== column) return 'unfold_more';
        return sortDirection === 'asc' ? 'expand_less' : 'expand_more';
    };

    return (
        <div>
            <div className="rev-table-head">
                <span>{t('eventStats.modal.email')}</span>
                <span>{t('eventStats.modal.name')}</span>
                <span className="rev-table-head__sortable" onClick={() => handleSort('photos')}>
                    {t('eventStats.modal.photos')}
                    <span className="material-icons rev-icon-sm">{sortIcon('photos')}</span>
                </span>
                <span className="rev-table-head__sortable" onClick={() => handleSort('status')}>
                    {t('eventStats.modal.status')}
                    <span className="material-icons rev-icon-sm">{sortIcon('status')}</span>
                </span>
            </div>
            {pagedUsers.map(user => (
                <div className="rev-table-row" key={user.id}>
                    <span className="rev-table-row__ellipsis">{user.email}</span>
                    <span className="rev-table-row__ellipsis">{user.name || ""}</span>
                    <span className="rev-num">{user.photos}</span>
                    <span>
                        <RevChip tone={SHARE_STATUS_TONE[user.status]}>{t(user.status)}</RevChip>
                    </span>
                </div>
            ))}
        </div>
    );
}

const PhotosByUserModal = ({ users, modal, setModal, loading }) => {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(0);
    const [sortColumn, setSortColumn] = useState('photos'); // 'photos' or 'status'
    // Por defecto, quien más fotos ha subido primero.
    const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'
    const [search, setSearch] = useState('');

    const sortData = (data) => {
        if (!data) return []; // Retorna un array vacío si data es undefined
        return [...data].sort((a, b) => {
            if (a[sortColumn] < b[sortColumn]) return sortDirection === 'asc' ? -1 : 1;
            if (a[sortColumn] > b[sortColumn]) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const handleSort = (column) => {
        setCurrentPage(0);
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection(column === 'photos' ? 'desc' : 'asc');
        }
    };

    // Usar sortData solamente si users está definido
    const sortedUsers = users ? sortData(users) : [];
    const filteredUsers = sortedUsers.filter(user => matchesEventsSearch(search, user.email, user.name));
    const pages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
    const pagedUsers = filteredUsers.slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE);
    const anyPhotos = sortedUsers.some(user => user.photos > 0);

    const onSearch = (e) => {
        setSearch(e.target.value);
        setCurrentPage(0);
    };

    const changePage = (page) => {
        setCurrentPage(page);
    };

    return (
        <RevModal open={modal} onClose={() => setModal(false)} closeLabel={t('eventStats.modal.close')}>
            {loading ? (
                <RevProgress aria-label={t('eventStats.modal.loading')} />
            ) : users && users.length === 0 ? (
                <p>{t('eventStats.modal.empty')}</p>
            ) : (
                <>
                    {anyPhotos
                        ? <PhotosByUserChart users={sortedUsers} />
                        : <p>{t('eventStats.modal.noPhotosYet')}</p>}
                    <div className="rev-toolbar">
                        <label className="rev-search">
                            <span className="material-icons rev-icon-sm rev-icon-muted">search</span>
                            <input
                                className="rev-search__input"
                                type="text"
                                placeholder={t('eventStats.modal.search.placeholder')}
                                aria-label={t('eventStats.modal.search.placeholder')}
                                value={search}
                                onChange={onSearch}
                            />
                        </label>
                    </div>
                    {filteredUsers.length === 0
                        ? <p>{t('eventStats.modal.search.empty')}</p>
                        : <PhotosByUserTable pagedUsers={pagedUsers} handleSort={handleSort} sortColumn={sortColumn} sortDirection={sortDirection} />}
                    <RevPagination page={currentPage} pages={pages} onChange={changePage}
                        prevLabel={t('eventStats.modal.prevPage')} nextLabel={t('eventStats.modal.nextPage')} />
                </>
            )}
        </RevModal>
    );
}


export default PhotosByUserModal;
